import { falCircleInfo } from 'shared/@generated/icons/falCircleInfo';
import dynamic from 'next/dynamic';
import type { Components } from 'react-markdown';
import type { Root } from 'hast';
import { toJsxRuntime } from 'hast-util-to-jsx-runtime';
import { Fragment, jsx, jsxs } from 'react/jsx-runtime';
import { Props } from 'shared/@types';
import { slugify } from 'shared/library/slugify';
import { FC, ReactNode, useMemo } from 'react';
import { Icon } from 'shared/components/icon';
import { Text } from 'shared/components/text';
import { View } from 'react-native';
import { AdBanner } from 'shared/components/ad-banner';
import { injectAdTagAfterHeadings } from 'shared/library/inject-ad-tag-after-headings';
import { QuoteCard } from 'shared/components/quote-card';

const MarkdownParser = dynamic(() => import('./markdown-parser.web'));

function containsElement(node: any, predicate: (node: { tagName?: string }) => boolean): boolean {
  if (!node || typeof node !== 'object') return false;

  if (node.type === 'element' && predicate(node)) {
    return true;
  }

  return Array.isArray(node.children)
    ? node.children.some((child: any) => containsElement(child, predicate))
    : false;
}

const MarkdownView = ({
  markdown,
  compiledTree,
  className,
  Header,
  Footer,
  customComponents,
  renderAds,
  skipFirstAdAfterHeading = true,
  minWordsBetweenAds = 150,
  adHeadingInterval,
}: Props<{
  markdown: string;
  /** Includes ad markers when requested by the caller. */
  compiledTree?: Root;
  Header?: ReactNode;
  Footer?: ReactNode;
  customComponents?: Record<string, FC>;
  renderAds?: boolean;
  skipFirstAdAfterHeading?: boolean;
  minWordsBetweenAds?: number;
  adHeadingInterval?: number;
}>) => {
  const markdownWithAds = useMemo(() => {
    if (compiledTree) return '';
    if (!renderAds) return markdown;
    return injectAdTagAfterHeadings(markdown, {
      skipFirstHeading: skipFirstAdAfterHeading,
      minWordsBetweenAds,
      headingInterval: adHeadingInterval,
    });
  }, [
    compiledTree,
    markdown,
    renderAds,
    skipFirstAdAfterHeading,
    minWordsBetweenAds,
    adHeadingInterval,
  ]);

  const components: Components = {
    h2: ({ node: _node, children, ...props }) => {
      const text = String(children);
      const id = slugify(text);
      return (
        <h2 id={id} {...props} className={'relative'}>
          <a
            href={`#${id}`}
            className="text-brand-brown-dark/30 hover:text-brand-brown-dark/75 mr-1.5 font-sans font-normal no-underline"
          >
            ¶
          </a>
          {children}
        </h2>
      );
    },
    // Article images have unknown dimensions: keep their natural size and ratio, shrink only to fit.
    img: ({ node: _node, ...props }: any) => (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        {...props}
        alt={props.alt ?? ''}
        className={'not-prose my-8 h-auto max-h-screen w-auto max-w-full rounded'}
        loading={'lazy'}
        decoding={'async'}
      />
    ),
    p: ({ node, children, ...props }) => {
      const hasImageChild = node?.children?.some((child: any) =>
        containsElement(child, (element) => element.tagName === 'img'),
      );

      const hasBlockComponent = node?.children?.some((child: any) =>
        containsElement(child, (element) => element.tagName?.startsWith('x-') === true),
      );

      if (hasImageChild || hasBlockComponent) {
        // A block wrapper keeps mixed text attached to an element during navigation,
        // even when browser translation has replaced the original text nodes.
        return <div {...props}>{children}</div>;
      }

      return <p {...props}>{children}</p>;
    },
    'x-callout': (props: any) => (
      <View className="my-6 rounded bg-blue-100 p-3 md:p-6">
        <View className={'flex-row items-center gap-3'}>
          <Icon icon={falCircleInfo} className="text-brand-brown-dark size-6 flex-shrink-0" />

          <Text className="text-brand-brown-dark font-semibold md:text-lg">
            {props.title ?? ''}
          </Text>
        </View>

        <Text className="mt-1.5 text-sm text-blue-950 md:text-base">{props.text ?? ''}</Text>
      </View>
    ),
    'x-quote': (props: any) => (
      <QuoteCard
        className={'my-3 bg-white md:my-6'}
        contentClassName={'pl-4 md:pl-6'}
        size={'large'}
        footer={
          props.from ? (
            <Text
              className={
                'text-brand-brown-dark/70 text-right leading-relaxed md:text-xl md:leading-relaxed'
              }
            >
              {props.from}
            </Text>
          ) : null
        }
      >
        <Text className={'text-brand-brown-dark leading-relaxed md:text-xl md:leading-relaxed'}>
          {props.text ?? ''}
        </Text>
      </QuoteCard>
    ),
    'x-ad': () => <AdBanner className={'not-prose my-6 md:my-9'} placement={'content'} />,
    ...(customComponents as any),
  };

  return (
    <article
      className={`prose prose-stone md:prose-lg font-regular prose-ul:mt-0 prose-ol:mt-0 prose-li:my-0.5 max-w-screen-md ${className}`}
    >
      {Header}

      {compiledTree ? (
        toJsxRuntime(compiledTree, {
          Fragment,
          jsx,
          jsxs,
          components,
          ignoreInvalidStyle: true,
          passKeys: true,
          passNode: true,
        })
      ) : (
        <MarkdownParser markdown={markdownWithAds} components={components} />
      )}

      {Footer}
    </article>
  );
};

export { MarkdownView };
